/**
 * data/repos/opportunitiesRepo — repositório da área `opportunities` (DD-004, Req 19).
 *
 * Resolve a fonte via `data/source.ts`:
 * - área real (`VITE_API_REAL_AREAS` contém `opportunities`, ou produção) → GET /v1/opportunities;
 * - caso contrário → mock tipado (`data/mock.ts`), sem chamada de rede.
 *
 * A fronteira de dados expõe `OpportunityListItem` — shape estável do portal,
 * com `Money` em centavos inteiros (RNF 11). O mapeamento DTO→portal vive aqui
 * (ponto único de tradução do contrato `/v1/opportunities`).
 *
 * Operações de escrita:
 * - `createOpportunity` → POST /v1/opportunities (body snake_case, centavos inteiros)
 * - `moveStage` → PATCH /v1/opportunities/{id}/stage (body { stage_id: UUID })
 *
 * Kanban:
 * - `buildKanbanBoard` constrói o `BoardState` do KanbanBoard client-side a partir
 *   de `GET /v1/opportunities` + `GET /v1/business-units/{buId}/stages`.
 *   Colunas são indexadas por stageId (UUID), ordenadas por `position`.
 *   Inclui colunas vazias para todos os estágios da BU. Quando `buId` não é
 *   fornecido (ou `listStages` falha), cai no agrupamento por `stageName` como fallback.
 */

import { apiClient } from "../../services/ApiClient.js";
import type { Money } from "../../shared/money/money.js";
import { resolveSource } from "../source.js";
import type { BoardState } from "../../features/pipeline/types.js";
import { listStages, listUsers } from "./platformRepo.js";
import { listAccounts } from "./accountsRepo.js";

/** Item de listagem de oportunidade projetado para o portal (Money em centavos). */
export interface OpportunityListItem {
  readonly id: string;
  readonly opportunityNumber: string;
  readonly title: string;
  readonly ownerId: string;
  readonly accountId: string;
  readonly stageName: string;
  /** UUID do estágio — presente quando o backend devolve `stage_id` no item. */
  readonly stageId?: string;
  readonly stageCategory: string;
  readonly valorTotal: Money;
  readonly forecastPonderado: Money;
  readonly isStale: boolean;
  readonly isOverdue: boolean;
  /** Data/hora de criação (ISO 8601). Campo `created_at` — habilita o KPI "Ciclo médio" (LDG-0009). */
  readonly createdAt?: string;
  /** Data/hora de encerramento (ISO 8601), ausente enquanto aberta. Campo `closed_at` (LDG-0009). */
  readonly closedAt?: string;
}

// ---------------------------------------------------------------------------
// Tipos para criação de oportunidade (POST /v1/opportunities)
// ---------------------------------------------------------------------------

/**
 * Input de criação de oportunidade para o portal.
 * Money em centavos inteiros (RNF 11). Snake_case é resolvido no mapeamento para o DTO.
 */
export interface CreateOpportunityInput {
  /** Título da oportunidade — obrigatório, sem PII (OP-ERR-001/011). */
  readonly title: string;
  /** GUID da conta — obrigatório (OP-ERR-001/010). */
  readonly accountId: string;
  /** GUID da BU — obrigatório (OP-ERR-001). */
  readonly buId: string;
  /** GUID do estágio inicial — obrigatório (OP-ERR-001). */
  readonly stageId: string;
  /** GUID do canal de origem — obrigatório (OP-ERR-003). */
  readonly originChannelId: string;
  /** GUID do responsável — obrigatório (OP-ERR-002). */
  readonly ownerId: string;
  /** Valor de setup em centavos (long). Padrão 0 (RNF 11). */
  readonly valorSetupCents: number;
  /** Valor mensal em centavos (long). Padrão 0 (RNF 11). */
  readonly valorMensalCents: number;
  /** Duração em meses — obrigatório quando valorMensalCents > 0 (OP-ERR-012). */
  readonly duracaoMeses: number;
  /** Probabilidade [0, 100] — null usa default do estágio. */
  readonly probabilidade?: number;
  /** Data de fechamento esperada (YYYY-MM-DD). */
  readonly expectedCloseDate?: string;
  /** Notas livres — sem PII (INV-13). */
  readonly notes?: string;
}

/** DTO para `POST /v1/opportunities` (snake_case, conforme JsonPropertyName no contrato). */
interface CreateOpportunityDto {
  account_id: string;
  bu_id: string;
  stage_id: string;
  origin_channel_id: string;
  owner_id: string;
  title: string;
  valor_setup: number;
  valor_mensal: number;
  duracao_meses: number;
  probabilidade?: number;
  expected_close_date?: string;
  notes?: string;
}

/** Resposta do `POST /v1/opportunities` — retorna id e número. */
interface CreateOpportunityResponse {
  id: string;
  opportunity_number?: string;
}

// ---------------------------------------------------------------------------
// Tipos para mover estágio (PATCH /v1/opportunities/{id}/stage)
// ---------------------------------------------------------------------------

/** DTO do body para `PATCH /v1/opportunities/{id}/stage`. */
interface MoveStageDto {
  stage_id: string;
}

// ---------------------------------------------------------------------------
// Tipo para o board Kanban montado client-side
// ---------------------------------------------------------------------------

/**
 * Resultado do `buildKanbanBoard`: estado do board + oportunidades mapeadas para
 * o shape `KanbanOpportunity` (esperado pelo KanbanBoard).
 */
export interface KanbanBoardData {
  /**
   * Estado do board com colunas indexadas por `stageId` (UUID).
   * Quando buId não é fornecido ou stages falham, as chaves são stageName (fallback).
   */
  readonly board: BoardState;
  /**
   * Mapa stageId → stageName para que o KanbanBoard exiba o nome correto
   * na coluna mesmo quando a chave da coluna é um UUID.
   */
  readonly stageNames: Readonly<Record<string, string>>;
  /**
   * Mapa userId → displayName (ou email como fallback) para resolução de nomes
   * nos cards e na ListView, evitando exibição de GUIDs.
   * Obtido via `listBuMembers(buId)` quando buId está disponível, ou `listUsers()`.
   * Em caso de falha na chamada, retorna mapa vazio (nunca quebra o board).
   */
  readonly ownerNames: Readonly<Record<string, string>>;
  /**
   * Mapa accountId → name para resolução de nomes nos cards e na ListView.
   * Obtido via `listAccounts({ page: 1, pageSize: 100 })` (máx. do account-management).
   * Em caso de falha na chamada, retorna mapa vazio (nunca quebra o board).
   */
  readonly accountNames: Readonly<Record<string, string>>;
  /**
   * Mapa accountId → tier (Onda B, item B3). Só populado quando o domínio de
   * origem expõe o campo (hoje: caminho mock). O domínio real `account-management`
   * (ADR-0009) NÃO retorna `tier` — nesse caso o mapa fica vazio e o card
   * simplesmente omite o badge de tier (nunca fabrica um valor).
   */
  readonly accountTiers?: Readonly<Record<string, string>>;
  /**
   * Mapa accountId → segmento (Onda B, item B3). Mesma ressalva de `accountTiers`:
   * vazio quando o domínio real não expõe o campo.
   */
  readonly accountSegments?: Readonly<Record<string, string>>;
  /**
   * Mapa stageId → categoria ("open" | "won" | "lost"). Permite ao KanbanBoard
   * rotear o drop em colunas terminais para os fluxos de ganhar/perder.
   * Vazio quando os estágios reais da BU não puderam ser carregados.
   */
  readonly stageCategories: Readonly<Record<string, string>>;
  /**
   * Oportunidades no formato `KanbanOpportunity` (id, name, account, value, owner, stage).
   * `value` em centavos inteiros (RNF 11). `stage` é o nome legível (não o UUID).
   */
  readonly opportunities: ReadonlyArray<{
    id: string;
    name: string;
    account: string;
    value: number;
    currency: Money["currency"];
    owner: string;
    stage: string;
    /** Data/hora de criação (ISO 8601) — habilita o KPI "Ciclo médio" (LDG-0009). */
    createdAt?: string;
    /** Data/hora de encerramento (ISO 8601), ausente enquanto aberta (LDG-0009). */
    closedAt?: string;
  }>;
}

/** Página de oportunidades projetada para o portal. */
export interface OpportunityPage {
  readonly items: readonly OpportunityListItem[];
  readonly totalCount: number;
  readonly page: number;
  readonly pageSize: number;
}

/** DTO bruto de um item do contrato `GET /v1/opportunities` (snake_case, cents long). */
interface OpportunitySummaryDto {
  id: string;
  opportunity_number: string;
  title: string;
  owner_id: string;
  account_id: string;
  stage_name: string;
  /** UUID do estágio — pode estar presente dependendo da versão do endpoint. */
  stage_id?: string;
  stage_category: string;
  valor_total: number;
  forecast_ponderado: number;
  is_stale: boolean;
  is_overdue: boolean;
  /** Presente desde LDG-0009. Opcional para tolerar respostas de contrato anterior. */
  created_at?: string;
  /** Presente desde LDG-0009 (nullable — ausente enquanto a oportunidade está aberta). */
  closed_at?: string | null;
}

/** DTO bruto da resposta paginada do contrato `GET /v1/opportunities`. */
interface OpportunityListDto {
  items?: OpportunitySummaryDto[];
  total_count?: number;
  page?: number;
  page_size?: number;
}

/** Parâmetros de listagem suportados (paginação — Req 19). */
export interface ListOpportunitiesParams {
  readonly page?: number;
  readonly pageSize?: number;
}

/** Traduz um item do DTO do backend para o shape estável do portal. */
function toListItem(dto: OpportunitySummaryDto): OpportunityListItem {
  // O backend devolve centavos inteiros (long). A moeda canônica do tenant é BRL
  // até o contrato `/v1` expor `currency` por linha (multimoeda — fora desta onda).
  const item: OpportunityListItem = {
    id: dto.id,
    opportunityNumber: dto.opportunity_number,
    title: dto.title,
    ownerId: dto.owner_id,
    accountId: dto.account_id,
    stageName: dto.stage_name,
    stageCategory: dto.stage_category,
    valorTotal: { amountCents: dto.valor_total, currency: "BRL" },
    forecastPonderado: { amountCents: dto.forecast_ponderado, currency: "BRL" },
    isStale: dto.is_stale,
    isOverdue: dto.is_overdue,
    ...(dto.created_at != null && { createdAt: dto.created_at }),
    ...(dto.closed_at != null && { closedAt: dto.closed_at }),
  };
  // Inclui stageId quando o backend devolve (permite mapeamento direto sem join por nome).
  if (dto.stage_id !== undefined) {
    return { ...item, stageId: dto.stage_id };
  }
  return item;
}

/** Projeta as oportunidades do mock para `OpportunityListItem` (caminho dev/preview). */
function toMockPage(
  mock: {
    opps: ReadonlyArray<{
      id: string;
      name: string;
      account: string;
      stage: string;
      value: number;
      prob: number;
      owner: string;
    }>;
    stages: ReadonlyArray<{ id: string; label: string }>;
  },
  params: ListOpportunitiesParams,
): OpportunityPage {
  const stageLabel = (id: string): string =>
    mock.stages.find((s) => s.id === id)?.label ?? id;
  const stageCategory = (id: string): string =>
    id === "won" ? "won" : id === "lost" ? "lost" : "open";

  const items: OpportunityListItem[] = mock.opps.map((o, i) => {
    const valorCents = Math.round(o.value * 100);
    return {
      id: o.id,
      opportunityNumber: `AZ-${String(i + 1).padStart(4, "0")}`,
      title: o.name,
      ownerId: o.owner,
      accountId: o.account,
      stageName: stageLabel(o.stage),
      stageCategory: stageCategory(o.stage),
      valorTotal: { amountCents: valorCents, currency: "BRL" },
      forecastPonderado: {
        amountCents: Math.round((valorCents * o.prob) / 100),
        currency: "BRL",
      },
      isStale: false,
      isOverdue: false,
    };
  });

  const page = Math.max(1, params.page ?? 1);
  const pageSize = Math.max(1, params.pageSize ?? items.length);
  const start = (page - 1) * pageSize;
  return {
    items: items.slice(start, start + pageSize),
    totalCount: items.length,
    page,
    pageSize,
  };
}

// ---------------------------------------------------------------------------
// Mapeadores internos
// ---------------------------------------------------------------------------

/** Mapeia o input do portal para o DTO wire do backend (snake_case). */
function toCreateDto(input: CreateOpportunityInput): CreateOpportunityDto {
  const dto: CreateOpportunityDto = {
    account_id: input.accountId,
    bu_id: input.buId,
    stage_id: input.stageId,
    origin_channel_id: input.originChannelId,
    owner_id: input.ownerId,
    title: input.title,
    valor_setup: input.valorSetupCents,
    valor_mensal: input.valorMensalCents,
    duracao_meses: input.duracaoMeses,
  };
  if (input.probabilidade !== undefined) dto.probabilidade = input.probabilidade;
  if (input.expectedCloseDate !== undefined) dto.expected_close_date = input.expectedCloseDate;
  if (input.notes !== undefined) dto.notes = input.notes;
  return dto;
}

// ---------------------------------------------------------------------------
// Funções exportadas — escrita
// ---------------------------------------------------------------------------

/**
 * Cria uma nova oportunidade. Real → `POST /v1/opportunities` (201 + Location/body).
 * O `ApiClient.create` lê o `id` do corpo ou do header `Location`.
 * Retorna o id da oportunidade criada.
 */
export async function createOpportunity(
  input: CreateOpportunityInput,
): Promise<{ id: string }> {
  const source = await resolveSource("opportunities");
  if (source.kind !== "api") {
    // No caminho mock, retorna id fictício para permitir testes de UI.
    return { id: `mock-${Date.now()}` };
  }
  const dto = toCreateDto(input);
  const result = await apiClient.create("/opportunities", dto);
  // apiClient.create lê id do body { id } ou do header Location (último segmento)
  const id = (result as CreateOpportunityResponse).id ?? result.id;
  return { id };
}

/**
 * Move uma oportunidade para um novo estágio.
 * Real → `PATCH /v1/opportunities/{id}/stage` com body `{ stage_id }`.
 * O `stageId` deve ser um UUID válido de estágio da BU.
 *
 * Nota: o `KanbanBoard` passa o `stageName` como `to`. Para persistir o move,
 * o chamador (PipelinePage) deve resolver o UUID real via o mapa de stages.
 * Quando o UUID não está disponível, o PATCH falha com 422 e o board reverte
 * otimisticamente (comportamento correto — WEB-ERR-007).
 */
export async function moveStage(
  opportunityId: string,
  stageId: string,
): Promise<void> {
  const source = await resolveSource("opportunities");
  if (source.kind !== "api") return; // No-op no caminho mock

  const body: MoveStageDto = { stage_id: stageId };
  await apiClient.patch<unknown>(`/opportunities/${opportunityId}/stage`, body);
}

// ---------------------------------------------------------------------------
// Função exportada — kanban board (construção client-side)
// ---------------------------------------------------------------------------

/**
 * Constrói o `BoardState` e a lista de oportunidades para o `KanbanBoard`.
 *
 * Quando `buId` é fornecido:
 * 1. Carrega oportunidades via `GET /v1/opportunities` (até 200 itens).
 * 2. Carrega estágios da BU via `GET /v1/business-units/{buId}/stages`.
 * 3. Cria colunas indexadas por `stageId` (UUID), incluindo colunas vazias.
 * 4. Ordena colunas por `position` (esquerda→direita).
 * 5. Associa cada oportunidade ao estágio via `stage_id` do DTO (quando presente)
 *    ou por correspondência de `stage_name` com o nome do estágio da BU.
 *
 * Fallback (sem `buId` ou quando `listStages` lança): agrupa por `stageName`,
 * preservando o comportamento anterior (open < won < lost por categoria).
 */
export async function buildKanbanBoard(
  buId?: string,
): Promise<KanbanBoardData> {
  const source = await resolveSource("opportunities");

  let allItems: readonly OpportunityListItem[];

  if (source.kind === "api") {
    const params: Record<string, number> = { page: 1, page_size: 200 };
    const dto = await apiClient.get<OpportunityListDto>("/opportunities", params);
    allItems = (dto.items ?? []).map(toListItem);
  } else {
    // Caminho mock: usa o mock carregado via resolveSource
    if (!source.mock) throw new Error("[repo] mock não disponível para área: opportunities");
    const result = toMockPage(source.mock, { page: 1, pageSize: 200 });
    allItems = result.items;
  }

  // ---------------------------------------------------------------------------
  // Carrega mapas de nomes (owner / account) — somente no caminho real (API).
  // No caminho mock os ids já são legíveis; chamar APIs reais de usuários/contas
  // quebraria o isolamento dos testes (que não mocam essas chamadas).
  // Falhas são absorvidas: mapas ficam vazios, nunca quebram o board.
  // ---------------------------------------------------------------------------

  const buildOwnerNames = async (): Promise<Record<string, string>> => {
    if (source.kind !== "api") return {};
    try {
      // Sempre busca tenant-wide via listUsers() para garantir que o responsável
      // seja resolvido mesmo quando não é membro da BU selecionada (#1).
      const users = await listUsers();
      return Object.fromEntries(
        users.map((u) => [u.id, u.displayName || u.email]),
      );
    } catch {
      return {};
    }
  };

  /**
   * Carrega nomes + tier + segmento das contas numa única varredura paginada.
   * `tier`/`segment` só vêm preenchidos quando o domínio de origem os expõe
   * (hoje: mock). O domínio real `account-management` não retorna esses campos
   * de enriquecimento (ADR-0009) — os mapas ficam vazios nesse caso, e o
   * `KanbanBoard` simplesmente omite o badge de tier/segmento (nunca fabrica).
   */
  const buildAccountEnrichment = async (): Promise<{
    names: Record<string, string>;
    tiers: Record<string, string>;
    segments: Record<string, string>;
  }> => {
    if (source.kind !== "api") return { names: {}, tiers: {}, segments: {} };
    try {
      // pageSize máximo aceito pelo account-management é 100 (ACC-ERR-002 acima disso).
      // Pagina até cobrir todas as contas do tenant para que cards de contas além
      // das 100 primeiras também exibam o nome (não o GUID). Limite de segurança
      // de 20 páginas (≤ 2000 contas) evita loop em caso de totalCount inconsistente.
      const names: Record<string, string> = {};
      const tiers: Record<string, string> = {};
      const segments: Record<string, string> = {};
      const pageSize = 100;
      for (let page = 1; page <= 20; page++) {
        const result = await listAccounts({ page, pageSize });
        for (const a of result.items) {
          names[a.id] = a.name;
          if (a.tierLabel) tiers[a.id] = a.tierLabel;
          if (a.segment) segments[a.id] = a.segment;
        }
        if (result.items.length < pageSize || page * pageSize >= result.totalCount) break;
      }
      return { names, tiers, segments };
    } catch {
      return { names: {}, tiers: {}, segments: {} };
    }
  };

  // ---------------------------------------------------------------------------
  // Tenta montar o board com estágios reais da BU (UUID como chave de coluna).
  // ---------------------------------------------------------------------------
  const stageNames: Record<string, string> = {};
  const stageCategories: Record<string, string> = {};
  const boardColumns: Record<string, string[]> = {};
  let boardStageOrder: string[] = [];

  let usedRealStages = false;

  // ownerNames e accountNames/tiers/segments são carregados em paralelo com listStages
  let ownerNames: Record<string, string> = {};
  let accountNames: Record<string, string> = {};
  let accountTiers: Record<string, string> = {};
  let accountSegments: Record<string, string> = {};

  if (buId) {
    // Carrega names em paralelo com os stages. buildOwnerNames/buildAccountEnrichment
    // já absorvem erros internamente (retornam {} em falha). listStages pode lançar
    // — isolamos no try/catch abaixo apenas o processamento dos stages.
    const [ownerNamesResult, accountEnrichmentResult] = await Promise.all([
      buildOwnerNames(),
      buildAccountEnrichment(),
    ]);
    ownerNames = ownerNamesResult;
    accountNames = accountEnrichmentResult.names;
    accountTiers = accountEnrichmentResult.tiers;
    accountSegments = accountEnrichmentResult.segments;

    try {
      const stageList = await listStages(buId);

      if (stageList.length > 0) {
        // Ordena por position (backend garante unicidade, mas ordenamos aqui também).
        const sorted = [...stageList].sort((a, b) => a.position - b.position);

        // Inicializa todas as colunas (inclusive as vazias).
        for (const stage of sorted) {
          boardColumns[stage.id] = [];
          boardStageOrder.push(stage.id);
          stageNames[stage.id] = stage.name;
          stageCategories[stage.id] = stage.category;
        }

        // Mapa nome → stageId para correspondência quando stage_id não vem no DTO.
        const nameToId = new Map<string, string>(
          sorted.map((s) => [s.name, s.id]),
        );

        // Distribui oportunidades nas colunas.
        for (const item of allItems) {
          const stageId = item.stageId ?? nameToId.get(item.stageName);
          if (stageId !== undefined && stageId in boardColumns) {
            boardColumns[stageId]?.push(item.id);
          }
          // Oportunidade cujo estágio não existe na BU é ignorada
          // (ex.: estágio removido; não cria coluna fantasma).
        }

        usedRealStages = true;
      }
    } catch {
      // listStages falhou (ex.: rede, 404) → cai no fallback por stageName.
    }
  }

  // ---------------------------------------------------------------------------
  // Fallback: agrupa por stageName (comportamento legado sem UUID de estágio).
  // Quando buId não está disponível, carrega ownerNames/accountNames em paralelo.
  // ---------------------------------------------------------------------------
  if (!usedRealStages) {
    // Carrega mapas de nomes (não foram carregados no bloco buId acima)
    if (!buId) {
      const [ownerNamesResult, accountEnrichmentResult] = await Promise.all([
        buildOwnerNames(),
        buildAccountEnrichment(),
      ]);
      ownerNames = ownerNamesResult;
      accountNames = accountEnrichmentResult.names;
      accountTiers = accountEnrichmentResult.tiers;
      accountSegments = accountEnrichmentResult.segments;
    }

    const stageOrderMap = new Map<string, number>();
    const stageCategoryMap = new Map<string, string>();

    for (const item of allItems) {
      // Chaveia a coluna pelo UUID do estágio (item.stageId) quando o backend o
      // devolve — assim o drag-and-drop envia sempre o stage_id (UUID) ao PATCH,
      // nunca o nome (que o backend rejeita como Guid inválido → 400). Só cai para
      // stageName se, por algum motivo, o stage_id não vier no item.
      const key = item.stageId ?? item.stageName;
      if (!stageOrderMap.has(key)) {
        stageOrderMap.set(key, stageOrderMap.size);
        boardColumns[key] = [];
        stageCategoryMap.set(key, item.stageCategory);
        stageNames[key] = item.stageName;
        stageCategories[key] = item.stageCategory;
      }
      boardColumns[key]?.push(item.id);
    }

    // Ordena: open < won < lost.
    const categoryOrder = (cat: string): number =>
      cat === "open" ? 0 : cat === "won" ? 1 : 2;

    boardStageOrder = [...stageOrderMap.keys()].sort((a, b) => {
      const catA = categoryOrder(stageCategoryMap.get(a) ?? "open");
      const catB = categoryOrder(stageCategoryMap.get(b) ?? "open");
      if (catA !== catB) return catA - catB;
      return (stageOrderMap.get(a) ?? 0) - (stageOrderMap.get(b) ?? 0);
    });
  }

  const board: BoardState = {
    columns: boardColumns,
    stageOrder: boardStageOrder,
    lastMove: null,
  };

  // Mapeia para o shape KanbanOpportunity esperado pelo KanbanBoard.
  // `value` em centavos inteiros (RNF 11); `account` e `owner` são GUIDs.
  // `stage` é o stageName legível (não o UUID) — usado na ListView.
  const opportunities = allItems.map((item) => ({
    id: item.id,
    name: item.title,
    account: item.accountId,
    value: item.valorTotal.amountCents,
    currency: item.valorTotal.currency,
    owner: item.ownerId,
    stage: item.stageName,
    ...(item.createdAt != null && { createdAt: item.createdAt }),
    ...(item.closedAt != null && { closedAt: item.closedAt }),
  }));

  return {
    board,
    stageNames,
    stageCategories,
    ownerNames,
    accountNames,
    accountTiers,
    accountSegments,
    opportunities,
  };
}

/**
 * Lista oportunidades. Real → `GET /v1/opportunities`; mock → `data/mock.ts`.
 * O `ApiClient` injeta `Authorization: Bearer` e headers de tenant automaticamente.
 */
export async function listOpportunities(
  params: ListOpportunitiesParams = {},
): Promise<OpportunityPage> {
  const source = await resolveSource("opportunities");
  if (source.kind === "api") {
    const query: Record<string, number> = {};
    if (params.page !== undefined) query["page"] = params.page;
    if (params.pageSize !== undefined) query["page_size"] = params.pageSize;
    const dto = await apiClient.get<OpportunityListDto>(
      "/opportunities",
      Object.keys(query).length > 0 ? query : undefined,
    );
    return {
      items: (dto.items ?? []).map(toListItem),
      totalCount: dto.total_count ?? 0,
      page: dto.page ?? 1,
      pageSize: dto.page_size ?? (dto.items?.length ?? 0),
    };
  }
  // Mock dev/preview: o módulo é carregado dinamicamente por resolveSource.
  if (!source.mock) throw new Error("[repo] mock não disponível para área: opportunities");
  return toMockPage(source.mock, params);
}
